import { Box, Flex, SimpleGrid, Text, Tooltip } from '@chakra-ui/react';
import { HelperTooltip } from 'components/Tooltip';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import DownloadExcelButton from './DownloadExcelButton';
import MarketingMonthCashflowSection from './MarketingMonthCashflowSection';
import { resolveDisplayedOutputMonthCount } from './calc/resolveDisplayedOutputMonthCount';
import {
  fmtMoney,
  fmtMoneyTable,
  fmtIrrPct,
  fmtExposurePct,
} from './calc/formatters';
import { resolveRequiredMaxExposure } from './calc/resolvePlanCovenantLimits';

function ResultsKpis({ calcResult, state, executiveView }) {
  if (executiveView) {
    return null;
  }
  const {
    totalIRR,
    totalWithdrawal,
    totalRepayments,
    runoffAtLastWithdrawal,
    endingCashAtLastWithdrawal,
    totalRevenue,
    peakTwelveBalance,
    lastDrawMonth,
    cols,
  } = calcResult;

  const lastDrawLabel =
    lastDrawMonth >= 0 ? cols[lastDrawMonth] : '—';

  const actualFee = totalRepayments - totalWithdrawal;

  const maxExposureLimit = resolveRequiredMaxExposure(state.maxExposure);

  const items = [
    {
      label: 'Total IRR',
      value: fmtIrrPct(totalIRR),
      footer: 'annualized',
      tooltip:
        'Annualized IRR on the Twelve net cashflow row: draws (Twelve funding out) versus repayments (cash back in). Fee income is included in repayments, not as a separate cashflow line.',
    },
    {
      label: 'Total withdrawal',
      value: fmtMoney(totalWithdrawal),
      footer: 'sum of Twelve fundings',
    },
    {
      label: 'Total repayments',
      value: fmtMoney(totalRepayments),
      footer: 'sum of cohort repayments',
    },
    {
      label: 'Actual fee',
      value: fmtMoney(actualFee),
      footer: 'repayments − withdrawals',
    },
    {
      label: 'Runoff at last withdrawal',
      value: fmtMoney(runoffAtLastWithdrawal),
      footer: `revenue tail after ${lastDrawLabel}`,
      tooltip: `Finds the last month with a Twelve draw (funding), then sums total Revenue from every month after that through the end of the forecast. Revenue includes new cohorts and any old-cohort revenue in the model.${lastDrawMonth >= 0 ? ` Last draw: ${lastDrawLabel}.` : ''}`,
    },
    {
      label: 'Ending cash at last withdrawal',
      value: fmtMoney(endingCashAtLastWithdrawal),
      footer: `at end of ${lastDrawLabel}`,
    },
    {
      label: 'Total revenue',
      value: fmtMoney(totalRevenue),
      footer: `Cum ROAS ${(state.roas[state.roas.length - 1] || 0).toFixed(0)}%`,
    },
    {
      label: 'Max exposure',
      value: fmtMoney(peakTwelveBalance),
      footer: `vs ${fmtMoney(maxExposureLimit)} max exposure`,
    },
  ];

  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
      {items.map((item) => (
        <Box key={item.label} className="kpi-card">
          <Flex align="center" gap={0}>
            <Text className="annotation">{item.label}</Text>
            {item.tooltip ? <HelperTooltip content={item.tooltip} /> : null}
          </Flex>
          <Text fontSize="2xl" fontWeight="bold" className="accent-text" mt={1}>
            {item.value}
          </Text>
          <Text fontSize="xs" color="gray.400" mt={1}>
            {item.footer}
          </Text>
        </Box>
      ))}
    </SimpleGrid>
  );
}

function ResultsTable({
  calcResult,
  incomeExpanded,
  onToggleIncome,
  executiveView,
  revenueDetailExpanded = false,
  onToggleRevenueDetail,
  exposureGradeLimits = null,
  expanded = true,
  onToggleExpanded,
  onDownloadExcel,
}) {
  const { cols } = calcResult;
  const displayMonthCount = useMemo(
    () => resolveDisplayedOutputMonthCount(calcResult),
    [calcResult]
  );
  const visibleCols = cols.slice(0, displayMonthCount);
  const hasRevenueBreakdown = Boolean(
    calcResult.revenueNewCohorts || calcResult.revenueOldCohorts
  );
  const showRevenueBreakdown = executiveView
    ? revenueDetailExpanded
    : incomeExpanded;
  const hideIncomeDetailRows = !executiveView && !incomeExpanded;
  const hideRevenueBreakdownRows =
    executiveView && hasRevenueBreakdown && !revenueDetailExpanded;

  const buildExposureGradeViolations = (arr, exposureLimitMaxPct) => {
    if (exposureLimitMaxPct == null) {
      return null;
    }
    const limit = Number(exposureLimitMaxPct);
    return arr.map((v) => {
      if (v == null || !Number.isFinite(Number(v)) || Number(v) <= limit) {
        return null;
      }
      return {
        tooltip: `Allowed max: ${fmtExposurePct(limit)} for your grade.`,
      };
    });
  };

  const row = (
    label,
    arr,
    {
      bold,
      indent,
      extraClass,
      hidden,
      totalValue,
      format = 'money',
      tooltip,
      totalDisplay,
      exposureLimitMaxPct,
      cellViolations,
      rowKey,
    } = {}
  ) => {
    const values = Array.isArray(arr) ? arr : [];
    const formatCell = (v) => {
      if (v == null) return '—';
      if (format === 'exposurePct') return fmtExposurePct(v);
      return fmtMoneyTable(v);
    };
    const resolvedViolations =
      cellViolations ??
      (exposureLimitMaxPct != null
        ? buildExposureGradeViolations(values, exposureLimitMaxPct)
        : null);
    const total =
      totalDisplay != null
        ? totalDisplay
        : totalValue != null
          ? totalValue
          : format === 'exposurePct'
            ? null
            : values.reduce((a, b) => a + (Number(b) || 0), 0);

    const renderCellContent = (v, i) => {
      const content = formatCell(v);
      const violation = resolvedViolations?.[i];
      if (!violation?.tooltip) {
        return content;
      }
      return (
        <Tooltip
          label={violation.tooltip}
          hasArrow
          placement="top"
          fontSize="xs"
          maxW="320px"
        >
          <span className="inline-block w-full">{content}</span>
        </Tooltip>
      );
    };

    return (
      <tr
        key={rowKey ?? label}
        className={`${extraClass || ''} ${hidden ? 'income-detail' : ''}`}
      >
        <td
          className={`label ${bold ? 'font-semibold' : ''} ${indent ? 'pl-8' : ''}`}
        >
          <Flex align="center" gap={0} display="inline-flex">
            {label}
            {tooltip ? <HelperTooltip content={tooltip} /> : null}
          </Flex>
        </td>
        {values.slice(0, displayMonthCount).map((v, i) => (
          <td
            key={`${label}-${i}`}
            className={`text-right ${bold ? 'font-semibold' : ''} ${
              format === 'money' && v < 0 ? 'negative' : ''
            } ${resolvedViolations?.[i] ? 'exposure-over-limit' : ''}`}
          >
            {renderCellContent(v, i)}
          </td>
        ))}
        <td className="text-right font-semibold bg-indigo-50">
          {total == null ? '—' : formatCell(total)}
        </td>
      </tr>
    );
  };

  return (
    <Box className="card overflow-hidden">
      <Flex
        className={`section-header${expanded ? '' : ' section-header-collapsed'}`}
        justify="space-between"
        align="center"
        gap={3}
        flexWrap="wrap"
      >
        <Box flex={1} minW={0}>
          <button
            type="button"
            className="section-toggle"
            onClick={onToggleExpanded}
          >
            {expanded ? '▼' : '▶'}{' '}
            {executiveView
              ? 'Standalone cashflow — monthly'
              : 'P&L and Twelve balance — monthly'}
          </button>
          {!expanded ? (
            <Text fontSize="xs" color="gray.500" mt={1}>
              {executiveView
                ? `Total EBITDA ${fmtMoney(calcResult.totalEbitda)}`
                : `Peak Twelve balance ${fmtMoney(calcResult.peakTwelveBalance)} · Total revenue ${fmtMoney(calcResult.totalRevenue)}`}
            </Text>
          ) : null}
        </Box>
        {onDownloadExcel ? (
          <DownloadExcelButton onDownload={onDownloadExcel} />
        ) : null}
      </Flex>
      {expanded ? (
      <Box overflowX="auto" px={3} pb={3}>
        <table
          className={`w-full table-output${hideIncomeDetailRows ? ' income-collapsed' : ''}${hideRevenueBreakdownRows ? ' revenue-collapsed' : ''}`}
        >
          <thead>
            <tr>
              <th className="text-left">Metric</th>
              {visibleCols.map((c) => (
                <th key={c} className="text-center">
                  {c}
                </th>
              ))}
              <th className="text-center">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="section-row">
              <td colSpan={displayMonthCount + 2} className="px-3 py-1.5">
                {executiveView ? (
                  'Income statement'
                ) : (
                  <button type="button" onClick={onToggleIncome}>
                    {incomeExpanded ? '▼' : '▶'} Income statement{' '}
                    <span className="annotation">
                      {incomeExpanded
                        ? '(click to collapse)'
                        : '(click to expand details)'}
                    </span>
                  </button>
                )}
              </td>
            </tr>
            {row('Revenue', calcResult.revenue, { bold: true })}
            {executiveView && hasRevenueBreakdown ? (
              <tr>
                <td colSpan={displayMonthCount + 2} className="px-3 py-1">
                  <button type="button" onClick={onToggleRevenueDetail}>
                    {revenueDetailExpanded ? '▼' : '▶'} Revenue breakdown{' '}
                    <span className="annotation">
                      {revenueDetailExpanded
                        ? '(hide new / old cohorts)'
                        : '(show new / old cohorts)'}
                    </span>
                  </button>
                </td>
              </tr>
            ) : null}
            {calcResult.revenueNewCohorts
              ? row('Revenue — new cohorts', calcResult.revenueNewCohorts, {
                  indent: true,
                  hidden: !showRevenueBreakdown,
                  extraClass: 'revenue-detail',
                })
              : null}
            {calcResult.revenueOldCohorts
              ? row('Revenue — old cohorts', calcResult.revenueOldCohorts, {
                  indent: true,
                  hidden: !showRevenueBreakdown,
                  extraClass: 'revenue-detail',
                })
              : null}
            {row('COGS', calcResult.cogs, {
              indent: true,
              hidden: !executiveView,
            })}
            {row('Gross profit', calcResult.grossProfit, {
              bold: true,
              hidden: !executiveView,
            })}
            {row('Marketing spend', calcResult.directMarketing, {
              indent: true,
              hidden: !executiveView,
            })}
            {row('Contribution', calcResult.contribution, {
              bold: true,
              hidden: !executiveView,
            })}
            {row('Opex', calcResult.opexRow, { indent: true, hidden: !executiveView })}
            {row('EBITDA', calcResult.ebitda, { bold: true, rowKey: 'income-ebitda' })}
            {!executiveView ? (
              <>
            <tr className="section-row">
              <td colSpan={displayMonthCount + 2} className="px-3 py-1.5">
                Twelve balance
              </td>
            </tr>
            {row('Beginning balance', calcResult.beginBal)}
            {row('Draw', calcResult.draw, {
              indent: true,
              cellViolations: calcResult.drawViolations,
            })}
            {row('Fee', calcResult.xirrFee, { indent: true })}
            {row('Facility fee', calcResult.facFeeRow, { indent: true })}
            {row('Repayment', calcResult.repay, { indent: true })}
            {row('Ending balance', calcResult.endBal, {
              bold: true,
              cellViolations: calcResult.endBalViolations,
            })}
              </>
            ) : null}
            <tr className="section-row">
              <td colSpan={displayMonthCount + 2} className="px-3 py-1.5">
                Cash
              </td>
            </tr>
            {row('Beginning cash', calcResult.beginCashRow, {
              totalValue: calcResult.beginCashTotal,
            })}
            {row('EBITDA', calcResult.ebitda, { rowKey: 'cash-ebitda' })}
            {!executiveView ? row('Twelve Finance', calcResult.twelveFinance, { bold: true }) : null}
            {row('Ending cash balance', calcResult.cashEnd, { bold: true })}
            {!executiveView ? (
              <>
            <tr>
              <td colSpan={displayMonthCount + 2} className="py-2" />
            </tr>
            <tr className="twelve-perspective-row">
              <td colSpan={displayMonthCount + 2} className="px-3 py-1.5">
                ⚡ Twelve perspective — IRR basis
              </td>
            </tr>
            {row('Twelve net cashflow', calcResult.twelveCF, { bold: true })}
            {row('Exposure to Twelve', calcResult.endBal, {
              indent: true,
              cellViolations: calcResult.endBalViolations,
              tooltip:
                'Ending Twelve balance for that month — the exposure amount used as the numerator in runrate and runoff exposure ratios below.',
            })}
            {row('Runrate', calcResult.runrate, {
              indent: true,
              tooltip:
                'This month’s total revenue × 12 (annualized run rate for that column).',
            })}
            {row('Runrate exposure', calcResult.runrateExposurePct, {
              indent: true,
              format: 'exposurePct',
              exposureLimitMaxPct: exposureGradeLimits?.runRateMaxPct,
              tooltip:
                'Ending Twelve balance ÷ runrate for that month, shown as %. Values above the allowed limit for your final score grade are highlighted.',
            })}
            {row('Runoff', calcResult.runoff, {
              indent: true,
              tooltip:
                'Sum of remaining revenue from this month through the end of the forecast, for all cohorts active through this month (same as summing each cohort row from this column onward in the cohort revenue sheet).',
            })}
            {row('Runoff exposure', calcResult.runoffExposurePct, {
              indent: true,
              format: 'exposurePct',
              exposureLimitMaxPct: exposureGradeLimits?.runoffMaxPct,
              tooltip:
                'Ending Twelve balance ÷ runoff for that month, shown as %. Values above the allowed limit for your final score grade are highlighted.',
            })}
            {row('Cash exposure', calcResult.cashExposurePct, {
              indent: true,
              format: 'exposurePct',
              tooltip:
                'Ending Twelve balance divided by ending cash for that month, shown as %.',
            })}
            {row('Runoff + cash exposure', calcResult.runoffPlusCashExposurePct, {
              indent: true,
              format: 'exposurePct',
              tooltip:
                'Ending Twelve balance divided by (ending cash + runoff) for that month, shown as %.',
            })}
            {calcResult.runoffExposureCohortCount != null ? (
              <>
                {row(
                  `Runoff (${calcResult.runoffExposureCohortCount} cohorts)`,
                  calcResult.runoffCohortWindow,
                  {
                    indent: true,
                    tooltip:
                      'Remaining revenue from this month onward for Twelve-funded cohorts active through this month. If fewer funded cohorts exist than the configured count, the window fills with the most recent unfunded plan cohorts and then old cohort-sheet rows (pre-plan revenue).',
                  }
                )}
                {row(
                  `Runoff exposure (${calcResult.runoffExposureCohortCount} cohorts)`,
                  calcResult.runoffCohortWindowExposurePct,
                  {
                    indent: true,
                    format: 'exposurePct',
                    exposureLimitMaxPct: exposureGradeLimits?.runoffMaxPct,
                    tooltip:
                      'Ending Twelve balance ÷ the cohort-window runoff above, shown as %.',
                  }
                )}
              </>
            ) : null}
              </>
            ) : null}
          </tbody>
        </table>
      </Box>
      ) : null}
    </Box>
  );
}

function ResultsSection({
  calcResult,
  calcTimestamp,
  state,
  incomeExpanded,
  onToggleIncome,
  revenueDetailExpanded = false,
  onToggleRevenueDetail,
  onExport,
  onShare,
  executiveView = false,
  exposureGradeLimits = null,
  onDownloadExcel,
}) {
  const [plTableExpanded, setPlTableExpanded] = useState(true);

  return (
    <Box className="results-section">
      <Flex align="center" justify="space-between" gap={3} mb={4} flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={3}>
          <Text fontSize="xl" fontWeight="bold" className="brand">
            Results
          </Text>
          {calcTimestamp && (
            <Text fontSize="xs" color="gray.400">
              Calculated {calcTimestamp.toLocaleTimeString()}
            </Text>
          )}
        </Box>
      </Flex>
      <ResultsKpis calcResult={calcResult} state={state} executiveView={executiveView} />
      <Box mt={6}>
        <ResultsTable
          calcResult={calcResult}
          incomeExpanded={incomeExpanded}
          onToggleIncome={onToggleIncome}
          executiveView={executiveView}
          revenueDetailExpanded={revenueDetailExpanded}
          onToggleRevenueDetail={onToggleRevenueDetail}
          exposureGradeLimits={exposureGradeLimits}
          expanded={plTableExpanded}
          onToggleExpanded={() => setPlTableExpanded((value) => !value)}
          onDownloadExcel={onDownloadExcel}
        />
      </Box>
      {!executiveView ? (
        <MarketingMonthCashflowSection calcResult={calcResult} state={state} />
      ) : null}
    </Box>
  );
}

ResultsSection.propTypes = {
  calcResult: PropTypes.object.isRequired,
  calcTimestamp: PropTypes.instanceOf(Date),
  state: PropTypes.object.isRequired,
  incomeExpanded: PropTypes.bool.isRequired,
  onToggleIncome: PropTypes.func.isRequired,
};

export default ResultsSection;
